import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from 'venn-ui-kit';
import { CombinePortfoliosWizard } from '../../CombinePortfoliosWizard/CombinePortfoliosWizard';
import { CreateAggregatePortfolioPanel } from '../../CreateAggregatePortfolioPanel/CreateAggregatePortfolioPanel';
import { isNil } from 'lodash';
import type { LibrarySearchEntity } from 'venn-api';
import { useNavigateToStudio } from 'venn-components';
import { fundSubject, portfolioSubject, privateFundSubject, privatePortfolioSubject, type Subject } from 'venn-state';
import { assertExhaustive, useHasFF } from 'venn-utils';
import type { CombinePortfoliosWizardConfiguration, OpenInDestination } from '../../CombinePortfoliosWizard/types';

const entityToSubject = (value: LibrarySearchEntity): Subject => {
  if (value.privateFundId) {
    return privateFundSubject(value.privateFundId);
  }
  if (value.privatePortfolioId) {
    return privatePortfolioSubject(value.privatePortfolioId);
  }
  if (value.fundId) {
    return fundSubject(value.fundId);
  }
  return portfolioSubject(value.portfolioId);
};

type PortfoliosTableActionProps = {
  selectedRows: LibrarySearchEntity[];
};

const PortfoliosTableAction: React.FC<PortfoliosTableActionProps> = ({ selectedRows }) => {
  const [combinePortfoliosConfiguration, setCombinePortfoliosConfiguration] =
    useState<CombinePortfoliosWizardConfiguration>();
  const [isCreateAggregatePortfolioOpen, setIsCreateAggregatePortfolioOpen] = useState(false);
  const navigateToReportLab = useNavigateToStudio('open in report lab RCM homepage', 'REPORT');
  const navigateToStudio = useNavigateToStudio('open in studio RCM homepage', 'TEARSHEET');
  const canShowAggregatePortfolioButton = useHasFF('rcm_portfolio_maintenance_ff');

  const publicPortfolioInformation = useMemo(
    () =>
      selectedRows
        .map(({ portfolioId, clientName, householdName }) => ({ id: portfolioId, clientName, householdName }))
        .filter(({ id }) => !isNil(id)),
    [selectedRows],
  );
  const selectedRowsCount = selectedRows.length;

  const onOpenIn = (destination: OpenInDestination) => {
    if (publicPortfolioInformation.length !== selectedRows.length) {
      const subjects = selectedRows.map(entityToSubject);
      switch (destination) {
        case 'report_lab':
          navigateToReportLab(subjects);
          return;
        case 'studio':
          navigateToStudio(subjects);
          return;
        default:
          throw assertExhaustive(destination);
      }
    } else {
      setCombinePortfoliosConfiguration({
        portfolioInformation: publicPortfolioInformation,
        destination,
      });
    }
  };

  const actionBtnClass = 'text-[14px] uppercase font-bold text-venn-dark-blue hover:text-venn-dark-blue';

  const createAggregatePortfolioButton =
    canShowAggregatePortfolioButton && publicPortfolioInformation.length > 1 ? (
      <Button variant="ghost" className={actionBtnClass} onClick={() => setIsCreateAggregatePortfolioOpen(true)}>
        <Icon type="folders" />
        Create Aggregate Portfolio ({selectedRowsCount})
      </Button>
    ) : null;

  return (
    <>
      <div className="px-[10px] h-[36px] flex items-center justify-start gap-[24px]">
        <div>{selectedRowsCount} selected</div>
        {selectedRowsCount ? (
          <div className="flex gap-[3px]">
            <Button variant="ghost" className={actionBtnClass} onClick={() => onOpenIn('report_lab')}>
              <Icon type="file-chart-column" />
              Open in Report lab ({selectedRowsCount})
            </Button>
            <Button variant="ghost" className={actionBtnClass} onClick={() => onOpenIn('studio')}>
              <Icon type="calculator-simple" />
              Open in Studio ({selectedRowsCount})
            </Button>
            {createAggregatePortfolioButton}
          </div>
        ) : (
          <div className="text-venn-grey-60">Select items to view actions</div>
        )}
      </div>
      {combinePortfoliosConfiguration && (
        <CombinePortfoliosWizard
          onClose={() => setCombinePortfoliosConfiguration(undefined)}
          configuration={combinePortfoliosConfiguration}
        />
      )}
      {isCreateAggregatePortfolioOpen && (
        <CreateAggregatePortfolioPanel
          onClose={() => setIsCreateAggregatePortfolioOpen(false)}
          portfolioInformation={publicPortfolioInformation}
        />
      )}
    </>
  );
};

export default PortfoliosTableAction;
